import { KeyTextField } from '@prismicio/client';
import { PlpHeroData } from '_components/PlpHero/types';
import type { DealsGridData } from '_components/Products/DealsGrid/types';
import { BrandedStoreSlice, Copy, PrismicImage } from './brandedStorePage';
import { ProductTagInfo } from './productTagInfo';
import { ProductStripDealsType } from './redesignTypes';

/**
 * PlpHeroData as it leaves the Prismic adapter: h1 is nullable because the
 * derived category-title fallback is only resolvable in getServerSideProps.
 */
export interface PlpHeroCmsData extends Omit<PlpHeroData, 'h1'> {
  h1: string | null;
  prismicId: string;
}
export interface PlpBannerData {
  heroImage: {
    dimensions: { width: number; height: number };
    url: string;
    alt: string;
  };
  videoId?: string;
  videoWidth?: string;
  videoHeight?: string;
  headline: string;
  tagline: string;
  callToActions: {
    cta_text: string;
    cta_url: string;
  }[];
}

export interface PlpMerchBoxesData {
  numberOfBoxes: string;
  boxes: {
    title: string;
    url: string;
    image: {
      alt: string;
      url: string;
      dimensions: {
        width: number;
        height: number;
      };
    };
  }[];
}

export enum Treatment {
  imageOnly = 'imageOnly',
  v1 = '1',
  v2 = '2',
  v3 = '3',
  takeoverTile = 'takeoverTile',
}

export enum TakeoverTileStyle {
  promoImage = 'promo_image',
  fullBleed = 'full_bleed',
}

export enum TakeoverTileSize {
  one = '1',
  two = '2',
}

interface ProductTileTakeoverBase {
  position: string;
  treatment: Treatment;
}

export interface ProductTileBase {
  id: string;
}

interface ImageOnlyProductTile extends ProductTileBase {
  backgroundImage?: PrismicImage;
  urlPath?: string;
  videoId?: KeyTextField;
  videoTitle?: KeyTextField;
  videoHeight?: number;
  videoWidth?: number;
  'co-op_brand_attribute'?: string;
}

interface HtmlPttProductTileBase extends ProductTileBase {
  logo?: PrismicImage;
  image?: PrismicImage;
  label?: KeyTextField;
  title?: KeyTextField;
  body?: KeyTextField;
  link?: {
    text: KeyTextField;
    url: KeyTextField;
  };
  linkType?: string;
  videoId?: KeyTextField;
  videoTitle?: KeyTextField;
  videoHeight?: number;
  videoWidth?: number;
  'co-op_brand_attribute'?: string;
}

interface HtmlPttV2WithOverlay {
  overlayFill?: string;
  textColour?: string;
}

export interface ImageOnlyPTT extends ProductTileTakeoverBase {
  product_tile: ImageOnlyProductTile;
  treatment: Treatment.imageOnly;
}

export interface HtmlPttV1 extends ProductTileTakeoverBase {
  product_tile: HtmlPttProductTileBase;
  treatment: Treatment.v1;
}

export interface HtmlPttV2 extends ProductTileTakeoverBase {
  product_tile: HtmlPttProductTileBase & HtmlPttV2WithOverlay;
  treatment: Treatment.v2;
}

export interface HtmlPttV3 extends ProductTileTakeoverBase {
  product_tile: HtmlPttProductTileBase;
  treatment: Treatment.v3;
}

interface TakeoverTileProductTile extends ProductTileBase {
  style: TakeoverTileStyle;
  size: TakeoverTileSize;
  image?: PrismicImage;
  headline?: KeyTextField;
  body?: KeyTextField;
  link?: {
    text: KeyTextField;
    url: KeyTextField;
  };
  'co-op_brand_attribute'?: string;
}

export interface TakeoverTilePTT extends ProductTileTakeoverBase {
  product_tile: TakeoverTileProductTile;
  treatment: Treatment.takeoverTile;
}

export type ProductTileTakeover =
  | ImageOnlyPTT
  | HtmlPttV1
  | HtmlPttV2
  | HtmlPttV3
  | TakeoverTilePTT;

export enum CategoryCarouselType {
  featured = 'featured',
  other = 'other',
  brands = 'brands',
}

export interface CategoryCarousel {
  label: string;
  url: string;
  image: PrismicImage;
}

export interface Video {
  id: string;
  video_type: 'mp4' | 'youtube';
  video: {
    url: string;
  };
  youtube_video_id?: string;
  position: string;
  display_video_controls: boolean;
  video_title?: string;
  fallback_image?: PrismicImage;
}

export interface CategoryCarouselItem {
  id: string;
  type: CategoryCarouselType;
  heading: string;
  items: CategoryCarousel[];
}

export interface EnhancedPlpBanner {
  title: string;
  copy: string;
  markdownCopy: Copy;
  ctaText: string;
  ctaUrl: string;
  desktopImage: PrismicImage;
  mobileImage: PrismicImage;
}

export interface ProductLineCarouselType {
  heading?: string;
  imageRatio?: string;
  items: {
    title: string;
    copy: Copy;
    product_line_name?: string;
    link?: string;
    image: PrismicImage;
  }[];
}
/** One curated product slot on the PLP cross-linking band. */
export interface PlpCrossLinkingItem {
  product_id: string;
  variant_sku: string | null;
}

/**
 * The cross-linking band as it leaves the Prismic adapter: authoring only. The
 * product data is resolved per request in `getServerSideProps` rather than here,
 * because this payload is served from a long-lived cache and prices must not go
 * stale — see {@link PlpCrossLinkingData}.
 */
export interface PlpCrossLinkingCmsData {
  id: string;
  heading: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  coopBrand: string | null;
  items: PlpCrossLinkingItem[];
}

export interface CollectionData {
  prismicId: string | null;
  plpBannerData: PlpBannerData | null;
  plpMerchBoxesData: PlpMerchBoxesData;
  seoDescription: string | null;
  plpPttData: ProductTileTakeover[];
  productTagInfoBannerData: ProductTagInfo;
  categoryCarousels: CategoryCarouselItem[];
  brandedStoreSlices: BrandedStoreSlice[];
  brandedStoreCoopAttribute: string | null;
  enhancedPlpBanner: EnhancedPlpBanner;
  videos: Video[];
  productLineCarousel: ProductLineCarouselType;
  /** Stacked deals strips, in render order; empty when the PLP has none. */
  productStripDeals: ProductStripDealsType[];
  /** Deals grid assigned to this PLP; `null` when none is assigned or it
   *  doesn't have enough complete cards for its layout. */
  dealsGrid: DealsGridData | null;
  plpHeroData: PlpHeroCmsData | null;
  /** Cross-linking band authoring; `null` when the PLP has none assigned. */
  plpCrossLinking: PlpCrossLinkingCmsData | null;
}
